/**
 * Page « Compte » — le compte connecté : mot de passe, second facteur,
 * appareils de confiance.
 *
 * Changer son mot de passe avant qu'il n'expire (rotation annuelle), oublier
 * les appareils de confiance (le code sera redemandé partout) et déconnecter
 * toutes les sessions ouvertes (elles durent 30 jours sinon). Le
 * second facteur ne se désactive pas d'ici : téléphone perdu = réinitialisation
 * sur le serveur (`create-admin --reset-mfa`).
 */

import { PASSWORD_POLICY_TEXT, passwordPolicyIssues, type AccountResponse } from '@shared/types/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { Card } from '../components/Card';
import { Layout } from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { changePassword, fetchAccount, forgetTrustedDevices, revokeAllSessions } from '../lib/api';
import { clearDeviceToken } from '../lib/auth';
import { formatDayShort, formatInstant } from '../lib/format';

function PasswordForm({ token, onSignedOut }: { token: string; onSignedOut: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const issues = passwordPolicyIssues(next);
    if (issues.length > 0) return setError(`Il manque : ${issues.join(', ')}.`);
    if (next !== confirm) return setError('Les deux saisies ne correspondent pas.');
    setBusy(true);
    try {
      await changePassword(token, current, next);
      // Nouveau mot de passe : l'API a revoque les sessions (celle-ci comprise)
      // et les appareils de confiance. Retour a la connexion, code compris.
      clearDeviceToken();
      onSignedOut();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Changement impossible.');
      setBusy(false);
    }
  };

  return (
    <form className="login__form account__form" onSubmit={(event) => void submit(event)}>
      {error && (
        <p className="login__error" role="alert">
          {error}
        </p>
      )}
      <div className="field">
        <label className="field__label" htmlFor="current-password">
          Mot de passe actuel
        </label>
        <input id="current-password" type="password" autoComplete="current-password" required value={current} onChange={(event) => setCurrent(event.target.value)} />
      </div>
      <div className="field">
        <label className="field__label" htmlFor="next-password">
          Nouveau mot de passe
        </label>
        <input id="next-password" type="password" autoComplete="new-password" required value={next} onChange={(event) => setNext(event.target.value)} />
        <span className="login__hint">{PASSWORD_POLICY_TEXT}</span>
      </div>
      <div className="field">
        <label className="field__label" htmlFor="confirm-password">
          Confirmer
        </label>
        <input id="confirm-password" type="password" autoComplete="new-password" required value={confirm} onChange={(event) => setConfirm(event.target.value)} />
      </div>
      <div>
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? 'Enregistrement...' : 'Changer le mot de passe'}
        </button>
      </div>
      <span className="login__hint">Tous les appareils seront déconnectés, celui-ci compris, et le code du second facteur redemandé.</span>
    </form>
  );
}

export function AccountPage() {
  const { email, token, signOut } = useAuth();
  const queryClient = useQueryClient();
  const account = useQuery<AccountResponse, Error>({
    queryKey: ['account', token],
    queryFn: ({ signal }) => fetchAccount(token ?? '', signal),
    enabled: token !== null,
  });
  const [forgetBusy, setForgetBusy] = useState(false);
  const [revokeBusy, setRevokeBusy] = useState(false);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['account'] });

  const forget = async () => {
    if (!token) return;
    setForgetBusy(true);
    try {
      await forgetTrustedDevices(token);
      clearDeviceToken();
      refresh();
    } finally {
      setForgetBusy(false);
    }
  };

  const revoke = async () => {
    if (!token) return;
    setRevokeBusy(true);
    try {
      await revokeAllSessions(token);
      signOut();
    } catch {
      setRevokeBusy(false);
    }
  };

  const data = account.data;
  const expiresSoon = data ? new Date(data.passwordExpiresAt).getTime() - Date.now() < 30 * 86_400_000 : false;

  return (
    <Layout email={email} onSignOut={signOut}>
      <h1 className="page-title">
        Compte
        <span className="page-title__sub">{email}</span>
      </h1>

      <div className="chart-grid">
        <Card title="Mot de passe" subtitle="Renouvelé tous les 365 jours. Passé ce délai, la connexion impose d'en choisir un nouveau.">
          {data && (
            <p className={expiresSoon ? 'notice notice--warning' : 'kpi__hint'}>
              Changé le {formatDayShort(data.passwordChangedAt.slice(0, 10))}, à renouveler avant le {formatDayShort(data.passwordExpiresAt.slice(0, 10))}.
            </p>
          )}
          {token && <PasswordForm token={token} onSignedOut={signOut} />}
        </Card>

        <Card title="Second facteur" subtitle="Code à six chiffres de l'application d'authentification, demandé à chaque connexion.">
          {account.isError && (
            <p className="notice notice--error" role="alert">
              Compte illisible : {account.error.message}
            </p>
          )}
          {data && (
            <>
              <p className="kpi__hint">
                {data.mfaEnabledAt ? `Activé le ${formatInstant(data.mfaEnabledAt)}.` : 'Pas encore activé : il le sera à la prochaine connexion.'}
                {data.admin.lastLoginAt ? ` Connexion précédente le ${formatInstant(data.admin.lastLoginAt)}.` : ''}
              </p>
              <p className="kpi__hint">
                {data.trustedDevices === 0
                  ? 'Aucun appareil de confiance : le code est demandé à chaque connexion.'
                  : `${data.trustedDevices} appareil${data.trustedDevices > 1 ? 's' : ''} de confiance (code non redemandé pendant 30 jours).`}
              </p>
              <div className="card__actions">
                <button type="button" className="btn" onClick={() => void forget()} disabled={forgetBusy || data.trustedDevices === 0}>
                  Oublier les appareils de confiance
                </button>
              </div>
              <p className="footnote">Téléphone perdu ? Le second facteur se réinitialise sur le serveur : `create-admin --reset-mfa`.</p>
            </>
          )}
        </Card>

        <Card title="Sessions" subtitle="Une connexion reste ouverte 30 jours sur chaque appareil.">
          <p className="kpi__hint">
            Appareil perdu, ordinateur partagé, doute : déconnecter tous les appareils, celui-ci compris. Le mot de passe sera redemandé partout.
          </p>
          <div className="card__actions">
            <button type="button" className="btn" onClick={() => void revoke()} disabled={revokeBusy || !token}>
              Déconnecter tous les appareils
            </button>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
